'use client';

import React, { FC } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import {
  CatCard,
  CatCheckboxField,
  CatHeading
} from 'components/blocks-components';
import { useTranslations } from 'next-intl';
import { AllFbtItems, CurrentItems } from 'services/fbtItems/types';
import { PRODUCT_FALLBACK_IMAGE } from 'constants/common';
import FbtFitment from '../../fbt-fitment';
import { Conditional } from '@cat-ecom/pcc-components/src/components/common/conditional';
import Price from 'components/price';
import { ProductDealerPriceAvailability } from 'services/dealerPriceAvailability/types';
import { useSelectItemEvent } from 'hooks/analytics/use-select-item-event';
import { getFormattedSearchSelectItem } from 'components/product-comparison-grid/product-comparison-card/utils';
import { FBT_LIST_NAME } from '../../../../constants/constants';
import { ProductsPriceData } from 'app/[lang]/[esite]/(core)/(search-display)/_components/analytics-listener/types';
import Styles from '../fbt-grid.module.scss';

interface FbtCardProps {
  product: AllFbtItems | CurrentItems;
  partPrice: ProductDealerPriceAvailability & ProductsPriceData;
  currency: string;
  isCatCorp: boolean;
  handleCheckboxChange?: (
    e: CustomEvent<{
      checked: boolean;
      indeterminate: boolean;
      value: string;
    }>
  ) => void;
  children?: React.ReactNode;
  orderItem;
  storeLocationId: string;
  manufacturerItem: Product;
  isRTL?: boolean;
  currencyLdFeatureFlag?: boolean;
}

const FbtCard: FC<FbtCardProps> = ({
  product,
  partPrice,
  currency,
  isCatCorp,
  handleCheckboxChange,
  children,
  orderItem,
  storeLocationId,
  manufacturerItem,
  isRTL = false,
  currencyLdFeatureFlag = false
}) => {
  const {
    name = '',
    partNumber = '',
    imageURL = '',
    isSerialCompatible = false
  } = product || {};

  const t = useTranslations();
  const { fireEvent: fireSelectItemEvent } = useSelectItemEvent();

  const handleSelectItemGa = () => {
    fireSelectItemEvent({
      item: getFormattedSearchSelectItem(orderItem, partPrice),
      partName: `${partPrice?.partNumber}: ${name}`,
      locationId: storeLocationId,
      itemSummary: {
        categoryPath: partPrice?.categoryPath,
        manufacturer: manufacturerItem.manufacturer
      },
      isSearchPageEvent: true,
      itemListId: FBT_LIST_NAME,
      itemListName: FBT_LIST_NAME,
      serialNumber: '',
      fitment: ''
    });
  };

  return (
    <CatCard>
      <div className="d-flex flex-column">
        <div className="d-flex align-items-start column-gap-1">
          {!isCatCorp ? (
            <CatCheckboxField
              checked={partPrice?.isChecked}
              disabled={!partPrice?.enableAddToCart}
              onBlCheckboxChange={handleCheckboxChange}
              id={partNumber}
            />
          ) : null}
          <div>
            <section id="this-item" className={`d-none ${Styles['this-item']}`}>
              <CatHeading
                variant="label-lg"
                className={`${Styles['this-item__heading']} text-capitalize`}
              >
                {`${t('PDP_THIS_PART')}:`}
              </CatHeading>
            </section>
            <Link
              href={partNumber}
              onClick={handleSelectItemGa}
              className={`text-decoration-underline cat-u-theme-typography-body ${Styles['c-fbt-card__title']}`}
            >{`${partNumber}: ${name}`}</Link>
          </div>
        </div>
        <div className="d-flex justify-content-center my-2">
          <Image
            src={imageURL || PRODUCT_FALLBACK_IMAGE}
            alt={partNumber}
            className={Styles['c-fbt-card__image']}
            loading="lazy"
            width={72}
            height={72}
            unoptimized
          />
        </div>
        <div className="d-flex flex-column">
          <FbtFitment isSerialCompatible={isSerialCompatible} />
          {!isCatCorp && (
            <Conditional test={partPrice?.enableAddToCart} fallback={children}>
              <Price
                price={partPrice}
                currency={currency}
                showDiscountSection={false}
                isRTL={isRTL}
                currencyLdFeatureFlag={currencyLdFeatureFlag}
              />
            </Conditional>
          )}
        </div>
      </div>
    </CatCard>
  );
};

export default FbtCard;
